import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import moment from "moment";
import "moment/locale/ko";
import { initReactI18next } from "react-i18next";

import enAccess from "./locales/en/access.json";
import enBill from "./locales/en/bill.json";
import enCommon from "./locales/en/common.json";
import enContract from "./locales/en/contract.json";
import enFacility from "./locales/en/facility.json";
import enUser from "./locales/en/user.json";
import enVisitor from "./locales/en/visitor.json";
import koAccess from "./locales/ko/access.json";
import koBill from "./locales/ko/bill.json";
import koCommon from "./locales/ko/common.json";
import koContract from "./locales/ko/contract.json";
import koFacility from "./locales/ko/facility.json";
import koUser from "./locales/ko/user.json";
import koVisitor from "./locales/ko/visitor.json";

/**
 * 지원 언어 목록. 기본(fallback)은 한국어.
 * RN 웹뷰가 URL 쿼리스트링(`?lang=en`)으로 언어를 전달한다.
 */
export const SUPPORTED_LANGUAGES = ["ko", "en"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = "ko";

/** localStorage 캐시 키 (쿼리스트링이 없는 내부 네비게이션에서 언어 유지) */
export const LANGUAGE_STORAGE_KEY = "court_lang";

/** 쿼리스트링에서 언어를 지정하는 파라미터 이름 (예: `?lang=en`) */
export const LANGUAGE_QUERY_PARAM = "lang";

const resources = {
  ko: {
    common: koCommon,
    contract: koContract,
    bill: koBill,
    user: koUser,
    access: koAccess,
    visitor: koVisitor,
    facility: koFacility,
  },
  en: {
    common: enCommon,
    contract: enContract,
    bill: enBill,
    user: enUser,
    access: enAccess,
    visitor: enVisitor,
    facility: enFacility,
  },
} as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // 감지 실패/미지원 언어일 때 한국어로 폴백
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // "en-US" 등 지역 코드가 들어와도 "en" 으로 매칭
    nonExplicitSupportedLngs: true,
    // 네임스페이스: 기본은 contract, 전역 문자열은 common
    ns: ["common", "contract", "bill", "user", "access", "visitor", "facility"],
    defaultNS: "contract",
    detection: {
      // 1) URL 쿼리스트링(?lang=en) 2) localStorage 캐시 순으로 감지
      order: ["querystring", "localStorage"],
      lookupQuerystring: LANGUAGE_QUERY_PARAM,
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // 감지된 언어를 localStorage 에 저장 → 이후 내부 이동에서도 유지
      caches: ["localStorage"],
    },
    interpolation: {
      // React 는 기본적으로 XSS 이스케이프하므로 i18next 이스케이프는 비활성화
      escapeValue: false,
    },
    react: {
      // 리소스를 번들에 정적 포함하므로 Suspense 불필요
      useSuspense: false,
    },
    returnNull: false,
  });

/**
 * 언어 변경 시 부수 효과 동기화:
 * - <html lang> 속성 갱신 (접근성/SEO)
 * - moment 전역 로케일 갱신 (날짜/요일 표기)
 *   ※ 개별 호출부에서 `.locale("ko")` 로 하드코딩된 곳은 화면 마이그레이션 시 별도 정리
 */
const syncSideEffects = (language: string) => {
  const lng = (SUPPORTED_LANGUAGES as readonly string[]).includes(language) ? language : DEFAULT_LANGUAGE;
  document.documentElement.lang = lng;
  moment.locale(lng);
};

syncSideEffects(i18n.language);
i18n.on("languageChanged", syncSideEffects);

export default i18n;
